import { useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CalendarCheck,
  CalendarDays,
  Check,
  ChevronRight,
  EyeOff,
  Info,
  LogOut,
  ShieldCheck,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { CalendarConnection } from "@/lib/calendar";
import {
  Badge,
  DarkPill,
  IconTile,
  OutlinePill,
  StepContextCard,
  calendarDialogClass,
  calendarOverlayClass,
} from "@/components/calendar/ui";

export type ConnectNotice = "missing_events" | "cancelled" | "failed" | null;

/**
 * J02 · Connect a calendar. Explains Google's three boxes before Google asks: two
 * needed, one optional for free times. Google opens in the same tab.
 */
export function ConnectCalendarDialog({
  open,
  onOpenChange,
  stepText,
  notice,
  continuing,
  onContinue,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The step being scheduled; absent when connecting from Settings. */
  stepText?: string;
  notice: ConnectNotice;
  continuing: boolean;
  onContinue: () => void;
}) {
  const [howOpen, setHowOpen] = useState(false);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent hideClose overlayClassName={calendarOverlayClass} className={cn(calendarDialogClass, "sm:max-w-[560px]")}>
          <div className="px-6 pb-6 pt-7 sm:px-8 sm:pt-8">
            {stepText && <StepContextCard label="Scheduling" text={stepText} className="mb-6" />}
            <DialogTitle className="font-display text-[26px] font-semibold tracking-[-0.02em] text-foreground">
              Connect a calendar
            </DialogTitle>
            <DialogDescription className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
              {stepText
                ? "Choose where this step should go. You will come back here to pick a time."
                : "Choose where your Next Steps should go. You'll pick a time for each one that fits your day."}
            </DialogDescription>

            {notice === "missing_events" && (
              <div role="alert" className="mt-5 flex gap-2.5 rounded-2xl bg-[#fbf1d6] px-4 py-3 text-[13.5px] leading-relaxed text-[#6d4f12]">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  UnfoldWIN needs <span className="font-semibold">Add events</span> to add your step. Nothing was saved —
                  try again and keep that box ticked on Google&apos;s screen.
                </p>
              </div>
            )}
            {notice === "cancelled" && (
              <p className="mt-5 flex items-center gap-2 rounded-2xl bg-foreground/[0.04] px-4 py-3 text-[13.5px] text-muted-foreground">
                <Info className="h-4 w-4 shrink-0" />
                Google was closed before connecting. Nothing was saved.
              </p>
            )}
            {notice === "failed" && (
              <p role="alert" className="mt-5 flex items-center gap-2 rounded-2xl bg-[#fbe6e6] px-4 py-3 text-[13.5px] text-[#8f2a33]">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Couldn&apos;t reach Google just now. Please try again.
              </p>
            )}

            <div className="mt-6 space-y-3">
              <button
                type="button"
                onClick={onContinue}
                disabled={continuing}
                className="flex w-full items-center gap-3 rounded-2xl border-[1.5px] border-primary/45 bg-white px-4 py-3.5 text-left shadow-[0_0_0_4px_rgba(110,83,172,0.06)] transition-colors hover:border-primary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <IconTile>
                  <CalendarDays className="h-[18px] w-[18px]" />
                </IconTile>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-medium text-foreground">Google Calendar</span>
                  <span className="block text-[12.5px] text-muted-foreground">Personal or Google Workspace accounts</span>
                </span>
                <ChevronRight className="h-4 w-4 text-foreground/60" />
              </button>
              <div aria-disabled className="flex items-center gap-3 rounded-2xl border border-foreground/[0.06] bg-[#faf8f5] px-4 py-3.5">
                <IconTile tone="muted" className="opacity-70">
                  <CalendarDays className="h-[18px] w-[18px]" />
                </IconTile>
                <span className="min-w-0 flex-1 opacity-60">
                  <span className="block text-[15px] font-medium text-foreground">Microsoft Outlook</span>
                  <span className="block text-[12.5px] text-muted-foreground">Personal, work or school accounts</span>
                </span>
                <Badge tone="muted">Coming soon</Badge>
              </div>
            </div>

            <div className="mt-5 grid gap-4 rounded-2xl bg-[#f4f1ec] p-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] sm:items-center">
              <ul aria-hidden className="space-y-2.5 rounded-xl border border-foreground/[0.06] bg-white px-3.5 py-3 text-[12.5px] text-foreground">
                {["See your list of calendars", "Add events to your calendars", "See when you're free or busy"].map((label) => (
                  <li key={label} className="flex items-center gap-2">
                    <span className="flex h-[15px] w-[15px] items-center justify-center rounded-[4px] bg-primary text-white">
                      <Check className="h-2.5 w-2.5" strokeWidth={3} />
                    </span>
                    {label}
                  </li>
                ))}
              </ul>
              <div>
                <p className="text-[14px] font-semibold text-foreground">Two needed, one optional</p>
                <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
                  Google asks for three permissions. UnfoldWIN needs the first two to add your step. The third lets it suggest
                  free times, and you can untick it. The wording on Google&apos;s screen may differ.
                </p>
              </div>
            </div>

            <div className="mt-5 flex gap-2.5 text-[13.5px] leading-relaxed text-foreground/85">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p>
                  UnfoldWIN checks when you&apos;re busy so it can suggest free times. It never sees the names, details or
                  attendees of your events, and it only creates the events you schedule.
                </p>
                <button
                  type="button"
                  onClick={() => setHowOpen(true)}
                  className="mt-1 font-medium text-primary underline underline-offset-4 hover:text-primary/80"
                >
                  How UnfoldWIN uses your calendar
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-foreground/[0.07] px-6 py-5 sm:flex-row sm:items-center sm:px-8">
            <p className="flex flex-1 items-start gap-1.5 text-xs leading-snug text-muted-foreground">
              <Info className="mt-px h-3.5 w-3.5 shrink-0" />
              Google opens in this tab, then brings you back.
            </p>
            <div className="flex gap-3">
              <OutlinePill className="flex-1 sm:flex-none" onClick={() => onOpenChange(false)}>
                Cancel
              </OutlinePill>
              <DarkPill className="flex-[2] sm:flex-none" loading={continuing} onClick={onContinue}>
                Continue to Google
                {!continuing && <ArrowRight className="h-4 w-4" />}
              </DarkPill>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <HowCalendarWorksDialog open={howOpen} onOpenChange={setHowOpen} />
    </>
  );
}

/**
 * J04 · Connected. Back from Google: the account and what it allowed, shown before
 * anything happens, with a way out if it's the wrong account.
 */
export function CalendarConnectedDialog({
  open,
  onOpenChange,
  connection,
  stepText,
  onFindTime,
  onDisconnect,
  disconnecting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connection: CalendarConnection | null;
  stepText?: string;
  /** Present when connecting from a step; otherwise the dialog ends with Done. */
  onFindTime?: () => void;
  onDisconnect: () => void;
  disconnecting: boolean;
}) {
  if (!connection) return null;
  const { account, access } = connection;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideClose overlayClassName={calendarOverlayClass} className={cn(calendarDialogClass, "sm:max-w-[480px]")}>
        <div className="px-6 pb-7 pt-7 sm:px-8 sm:pt-8">
          <IconTile tone="green" size="lg" className="rounded-full">
            <CalendarCheck className="h-6 w-6" />
          </IconTile>
          <DialogTitle className="mt-5 font-display text-[26px] font-semibold tracking-[-0.02em] text-foreground">
            Google Calendar connected
          </DialogTitle>
          <DialogDescription className="mt-1.5 text-[15px] text-muted-foreground">
            Check this is the account you meant to connect.
          </DialogDescription>

          <div className="mt-6 flex items-center gap-3 rounded-2xl border border-foreground/[0.08] px-4 py-3.5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/12 text-[17px] font-semibold text-primary">
              {(account.name || account.email).charAt(0).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px] font-medium text-foreground">{account.name}</span>
              <span className="block truncate text-[13px] text-muted-foreground">{account.email}</span>
            </span>
            <Badge tone="green" icon={<Check className="h-3.5 w-3.5" />}>
              Connected
            </Badge>
          </div>

          <div className="mt-4 overflow-hidden rounded-2xl border border-foreground/[0.08]">
            <div className="flex items-center gap-3 px-4 py-3">
              <CalendarDays className="h-4 w-4 shrink-0 text-primary" />
              <span className="flex-1 text-[14px] text-foreground">Add events to your calendars</span>
              <Badge tone="green" icon={<Check className="h-3 w-3" />} className="py-0.5">
                Allowed
              </Badge>
            </div>
            <div className="flex items-center gap-3 border-t border-foreground/[0.06] px-4 py-3">
              <EyeOff className="h-4 w-4 shrink-0 text-primary" />
              <span className="flex-1 text-[14px] text-foreground">See when you&apos;re busy</span>
              {access.freeBusy ? (
                <Badge tone="green" icon={<Check className="h-3 w-3" />} className="py-0.5">
                  Allowed
                </Badge>
              ) : (
                <Badge tone="muted" className="py-0.5">
                  Not allowed
                </Badge>
              )}
            </div>
            <p className="border-t border-foreground/[0.06] bg-primary/[0.05] px-4 py-2.5 text-[12.5px] text-muted-foreground">
              {access.freeBusy
                ? "Busy times only. Event names and details stay private."
                : "UnfoldWIN will show common times instead, and you pick. You can allow it later in Settings."}
            </p>
          </div>

          <p className="mt-4 text-[13.5px] text-muted-foreground">
            Not the right account?{" "}
            <button
              type="button"
              onClick={onDisconnect}
              disabled={disconnecting}
              className="font-medium text-primary underline underline-offset-4 hover:text-primary/80 disabled:opacity-50"
            >
              {disconnecting ? "Disconnecting…" : "Disconnect"}
            </button>
          </p>

          {stepText && <StepContextCard label="Ready to schedule" text={stepText} className="mt-5" />}

          <DarkPill size="lg" className="mt-5 w-full" onClick={onFindTime ?? (() => onOpenChange(false))}>
            {onFindTime ? (
              <>
                Find a time <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              "Done"
            )}
          </DarkPill>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** J16 · Disconnect. Removing access never removes events already added. */
export function DisconnectCalendarDialog({
  open,
  onOpenChange,
  email,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  pending: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideClose overlayClassName={calendarOverlayClass} className={cn(calendarDialogClass, "sm:max-w-[460px]")}>
        <div className="px-6 pb-7 pt-7 sm:px-8 sm:pt-8">
          <IconTile tone="red" size="lg" className="rounded-full">
            <LogOut className="h-5 w-5" />
          </IconTile>
          <DialogTitle className="mt-5 font-display text-[24px] font-semibold tracking-[-0.02em] text-foreground">
            Disconnect Google Calendar?
          </DialogTitle>
          <DialogDescription className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            UnfoldWIN will no longer add Next Steps to <span className="[overflow-wrap:anywhere]">{email}</span> or check when
            you&apos;re busy.
          </DialogDescription>
          <p className="mt-4 flex gap-2.5 rounded-2xl bg-[#f4f1ec] px-4 py-3 text-[13.5px] leading-relaxed text-foreground/85">
            <CalendarCheck className="mt-0.5 h-4 w-4 shrink-0" />
            Events already added stay in your calendar. You can remove them there.
          </p>
          <div className="mt-6 flex justify-end gap-3">
            <OutlinePill onClick={() => onOpenChange(false)}>Keep connected</OutlinePill>
            <DarkPill loading={pending} onClick={onConfirm} className="bg-[#a8323e] hover:bg-[#a8323e]/90">
              Disconnect
            </DarkPill>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** "How UnfoldWIN uses your calendar" — the plain-language version of the permissions. */
export function HowCalendarWorksDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const rows = [
    {
      title: "Your list of calendars",
      body: "So you can choose where a step goes. UnfoldWIN shows their names only to you.",
    },
    {
      title: "Adding events",
      body: "Only the steps you schedule, word for word, marked “Added from UnfoldWIN”. It never edits or deletes anything else.",
    },
    {
      title: "When you're busy (optional)",
      body: "Busy and free times only — never the names, details or attendees of your events — so it can suggest times that fit.",
    },
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent overlayClassName={cn(calendarOverlayClass, "z-[110]")} className={cn(calendarDialogClass, "z-[111] sm:max-w-[480px]")}>
        <div className="px-6 pb-7 pt-7 sm:px-8 sm:pt-8">
          <IconTile size="lg" className="rounded-full">
            <ShieldCheck className="h-5 w-5" />
          </IconTile>
          <DialogTitle className="mt-5 font-display text-[22px] font-semibold tracking-[-0.02em] text-foreground">
            How UnfoldWIN uses your calendar
          </DialogTitle>
          <ul className="mt-5 space-y-4">
            {rows.map((r) => (
              <li key={r.title}>
                <p className="text-[14px] font-semibold text-foreground">{r.title}</p>
                <p className="mt-0.5 text-[13.5px] leading-relaxed text-muted-foreground">{r.body}</p>
              </li>
            ))}
          </ul>
          <p className="mt-5 rounded-2xl bg-primary/[0.06] px-4 py-3 text-[13px] leading-relaxed text-foreground/80">
            Disconnect any time in Settings. Events already added stay in your calendar; free-time suggestions can be switched
            off without disconnecting.
          </p>
          <DarkPill className="mt-6 w-full" onClick={() => onOpenChange(false)}>
            Got it
          </DarkPill>
        </div>
      </DialogContent>
    </Dialog>
  );
}
